import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Camera, Loader2, Sparkles, Upload } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Label } from "../ui/label";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TIPI_LABEL = {
  parrucchiere: "Parrucchiere",
  podologo: "Podologo",
  visita_specialistica: "Visita Specialistica",
  acquisto_ausili: "Acquisto Ausili",
  altro: "Altro",
};

const ALLOWED_EXT = [".jpg", ".jpeg", ".png", ".webp", ".gif", ".pdf"];

function formatDateIt(iso) {
  if (!iso) return "—";
  const d = new Date(`${String(iso).slice(0, 10)}T12:00:00`);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString("it-IT");
}

function formatImporto(v) {
  if (v == null || v === "") return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return n.toLocaleString("it-IT", { style: "currency", currency: "EUR" });
}

function validateFile(file) {
  if (!file) return null;
  const ext = `.${(file.name.split(".").pop() || "").toLowerCase()}`;
  if (!ALLOWED_EXT.includes(ext)) {
    return `Formato non supportato (${ext}). Usa JPG, PNG o PDF.`;
  }
  if (file.size > 50 * 1024 * 1024) {
    return "File troppo grande (max 50 MB)";
  }
  return null;
}

/** Abbina un nome estratto dallo scontrino a un ospite della lista. */
export function matchOspiteFromNome(ospiteNome, ospiti = []) {
  const norm = (s) =>
    String(s || "")
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9\s]/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  const q = norm(ospiteNome);
  if (!q || !ospiti.length) return "";

  const scored = ospiti
    .map((o) => {
      const full = norm(o.nome_completo || `${o.nome || ""} ${o.cognome || ""}`);
      if (!full) return { id: o.id, score: 0 };
      if (full === q) return { id: o.id, score: 100 };
      if (full.includes(q) || q.includes(full)) return { id: o.id, score: 80 };
      const parts = q.split(" ").filter((p) => p.length > 1);
      const hits = parts.filter((p) => full.includes(p)).length;
      return { id: o.id, score: hits >= 2 ? 60 + hits : hits === 1 && parts.length === 1 ? 40 : 0 };
    })
    .filter((x) => x.score > 0)
    .sort((a, b) => b.score - a.score);

  return scored[0]?.score >= 40 ? scored[0].id : "";
}

export default function EstraiSpesaDaScontrinoDialog({
  open,
  onOpenChange,
  token,
  apiPath = "/api/prima-nota/spese/extract-scontrino",
  ospiti = [],
  onApply,
}) {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open) {
      setFile(null);
      setResult(null);
      setAnalyzing(false);
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const previewRows = useMemo(() => {
    const f = result?.fields;
    if (!f) return [];
    const matchedId = matchOspiteFromNome(f.ospite_nome, ospiti);
    const matched = ospiti.find((o) => o.id === matchedId);
    return [
      { label: "Tipo", value: TIPI_LABEL[f.tipo] || f.tipo || "—" },
      { label: "Importo", value: formatImporto(f.importo) },
      { label: "Data", value: formatDateIt(f.data) },
      { label: "Descrizione", value: f.descrizione || "—" },
      { label: "Note", value: f.note || "—" },
      {
        label: "Ospite (da scontrino)",
        value: f.ospite_nome
          ? matched
            ? `${f.ospite_nome} → ${matched.nome_completo}`
            : f.ospite_nome
          : "—",
      },
    ];
  }, [result, ospiti]);

  const handleFile = (selected) => {
    const err = validateFile(selected);
    if (err) {
      toast.error(err);
      return;
    }
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setFile(selected);
    setResult(null);
    setPreviewUrl(selected && selected.type?.startsWith("image/") ? URL.createObjectURL(selected) : null);
  };

  const analyze = async () => {
    if (!file || !token) return;
    setAnalyzing(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API_URL}${apiPath}`, fd, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      if (data.warnings?.length) {
        data.warnings.forEach((w) => toast.message(w));
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore analisi scontrino");
    } finally {
      setAnalyzing(false);
    }
  };

  const apply = () => {
    const f = result?.fields;
    if (!f) return;
    const ospiteId = matchOspiteFromNome(f.ospite_nome, ospiti);
    onApply?.({
      tipo: f.tipo || "",
      importo: f.importo != null ? String(f.importo) : "",
      data: f.data || "",
      descrizione: f.descrizione || "",
      note: f.note || "",
      ospiteId,
      ospite_nome: f.ospite_nome || "",
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-rose-500" />
            Spesa da scontrino
          </DialogTitle>
          <DialogDescription>
            Fotografa o carica lo scontrino: l&apos;IA propone tipo, importo, data e descrizione. Verifica sempre prima di salvare.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-1">
          <div className="space-y-2">
            <Label>Foto o file</Label>
            <input
              ref={inputRef}
              type="file"
              accept="image/*,.pdf,application/pdf"
              capture="environment"
              className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0] || null)}
            />
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="outline"
                className="gap-2"
                onClick={() => inputRef.current?.click()}
                disabled={analyzing}
              >
                <Camera className="w-4 h-4" />
                Scatta / scegli
              </Button>
              {file ? (
                <span className="text-xs text-slate-500 self-center truncate max-w-[180px]">
                  {file.name}
                </span>
              ) : null}
            </div>
            {previewUrl ? (
              <img
                src={previewUrl}
                alt="Anteprima scontrino"
                className="mt-2 max-h-40 w-full object-contain rounded-lg border border-slate-200 bg-slate-50"
              />
            ) : file ? (
              <div className="flex items-center gap-2 text-sm text-slate-600 rounded-lg border border-dashed border-slate-200 p-3">
                <Upload className="w-4 h-4 shrink-0" />
                PDF pronto per l&apos;analisi
              </div>
            ) : null}
          </div>

          {result?.fields ? (
            <div className="rounded-xl border border-rose-100 bg-rose-50/40 p-3 space-y-1.5">
              <p className="text-xs font-semibold uppercase tracking-wide text-rose-700">Dati riconosciuti</p>
              {previewRows.map((row) => (
                <div key={row.label} className="flex justify-between gap-3 text-sm">
                  <span className="text-slate-500 shrink-0">{row.label}</span>
                  <span className="text-slate-800 font-medium text-right">{row.value}</span>
                </div>
              ))}
            </div>
          ) : null}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={analyzing}>
            Annulla
          </Button>
          {!result?.fields ? (
            <Button
              type="button"
              onClick={analyze}
              disabled={!file || analyzing}
              className="bg-rose-600 hover:bg-rose-700 gap-2"
            >
              {analyzing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {analyzing ? "Analisi…" : "Analizza con IA"}
            </Button>
          ) : (
            <Button type="button" onClick={apply} className="bg-rose-600 hover:bg-rose-700">
              Usa questi dati
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
